import React, { FC, useRef, useState } from 'react';
import { ReactComponent as UploadIcon } from '../../icons/upload-cloud-02.svg';
import {
  MAX_UPLOAD_FILES,
  UPLOAD_ACCEPT,
  formatBytes,
  uploadProblem,
} from './handbookImports';
import './handbook-upload-field.scss';

interface HandbookUploadFieldProps {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
  /** Visible label above the drop area */
  label?: string;
  /** Files the backend will add on top of these (counts toward the limit) */
  reservedSlots?: number;
}

/** Adds files to a list, skipping ones already chosen (same name and size). */
export function mergeFiles(current: File[], incoming: File[]): File[] {
  const seen = new Set(current.map((f) => `${f.name}:${f.size}`));
  return [
    ...current,
    ...incoming.filter((f) => !seen.has(`${f.name}:${f.size}`)),
  ];
}

/** Why these files cannot be sent, or null; `reservedSlots` lowers the file cap. */
export function uploadFieldProblem(
  files: File[],
  reservedSlots = 0,
): string | null {
  const cap = MAX_UPLOAD_FILES - reservedSlots;
  if (files.length > cap) {
    return `Up to ${cap} files per import; remove ${files.length - cap}.`;
  }
  return uploadProblem(files);
}

/**
 * The one place files are chosen for the Handbook: a drop area with a file
 * picker and the list of chosen files. Shown inline in the guided setup and
 * in the Import dialog, so neither stacks another dialog on top.
 */
export const HandbookUploadField: FC<HandbookUploadFieldProps> = ({
  files,
  onChange,
  disabled = false,
  label = 'Files',
  reservedSlots = 0,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const problem = uploadFieldProblem(files, reservedSlots);
  const cap = MAX_UPLOAD_FILES - reservedSlots;

  const add = (incoming: FileList | null) => {
    if (!incoming || incoming.length === 0) return;
    onChange(mergeFiles(files, Array.from(incoming)));
  };

  return (
    <div className='handbook-upload'>
      <span className='handbook-upload-label' id='handbook-upload-label'>
        {label}
      </span>
      <div
        className={`handbook-upload-drop${dragging ? ' dragging' : ''}`}
        data-testid='handbook-dropzone'
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (!disabled) add(e.dataTransfer.files);
        }}
      >
        <UploadIcon aria-hidden='true' />
        <p className='handbook-upload-text'>
          <button
            type='button'
            className='handbook-upload-choose'
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          >
            Choose files
          </button>{' '}
          or drag them here
        </p>
        <p className='handbook-upload-hint'>
          PDF, Word, Markdown, text or HTML. Up to {cap} files, 25 MB each.
        </p>
        <input
          ref={inputRef}
          type='file'
          multiple
          accept={UPLOAD_ACCEPT}
          aria-labelledby='handbook-upload-label'
          className='handbook-upload-input'
          disabled={disabled}
          onChange={(e) => {
            add(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
      {files.length > 0 && (
        <ul className='handbook-upload-list' aria-label='Chosen files'>
          {files.map((f, index) => (
            <li key={`${f.name}:${f.size}:${index}`}>
              <span className='handbook-upload-name'>{f.name}</span>
              <span className='handbook-upload-size'>
                {formatBytes(f.size)}
              </span>
              <button
                type='button'
                className='handbook-upload-remove'
                disabled={disabled}
                aria-label={`Remove ${f.name}`}
                onClick={() => onChange(files.filter((_, at) => at !== index))}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      )}
      {problem && files.length > 0 && (
        <p
          className='handbook-upload-problem'
          role='status'
          data-testid='handbook-import-problem'
        >
          {problem}
        </p>
      )}
    </div>
  );
};
